<template>
  <FilterComponent
    :open="!!open"
    @change:state="(value) => $emit('change:state', value)"
    :fixed="true"
  >
    <v-layout row justify-space-between>
      <div class="font-weight-thin text-uppercase title pa-3">
        {{ $t("Filter") }}
      </div>
      <div>
        <v-btn color="accent" @click="applyFilter" dark>{{
          $t("APPLY")
        }}</v-btn>
      </div>
    </v-layout>
    <v-divider class="mb-2"></v-divider>
    <v-flex xs12>
      <Card class="pa-3" :key="forceReloadFilter">
        <div v-for="filter in currentFilter" :key="filter.key">
          <div v-if="filter.value && filter.type == 'select'">
            <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
            <v-select
              :items="selectRange(filter)"
              :value="activeParameters[`${filter.id}`]"
              :label="$t(filter.title)"
              @change="onChangeHandler($event, filter)"
              :item-text="(options) => $t(options.text)"
              class="white--text"
              color="primary"
              outline
              return-object
            >
            </v-select>
          </div>
          <div v-if="filter.value && filter.type == 'selectCombo'">
            <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
            <v-select
              :items="selectRange(filter)"
              :value="activeParameters[`${filter.id}`]"
              :item-text="(options) => $t(options.text)"
              item-value="modifiedId"
              @change="onChangeHandler($event, filter)"
              :label="$t(filter.title)"
              class="white--text"
              return-object
              multiple
              outline
              required
            >
              <template slot="prepend-item">
                <v-list-tile ripple @click="toggleSelectAll(filter)">
                  <v-list-tile-action>
                    <v-icon color="indigo darken-4"
                      >mdi-checkbox-blank-outline</v-icon
                    >
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title
                      v-if="
                        activeParameters[`${filter.id}`].length ==
                        selectRange(filter).length
                      "
                      >{{ $t("Deselect All") }}
                    </v-list-tile-title>
                    <v-list-tile-title v-else>{{
                      $t("Select All")
                    }}</v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-divider class="mt-2"></v-divider>
              </template>

              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0">
                  {{ item.text }}
                </span>
                <span v-if="index === 1" class="grey--text caption"
                  >(+{{ activeParameters[`${filter.id}`].length - 1 }}
                  {{ $t("others") }})</span
                >
              </template>
            </v-select>
          </div>

          <div v-else-if="filter.type == 'datePicker'">
            <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
            <RangeDatePicker
              @change:date="onChangeHandler($event, filter)"
              :fullPicker="true"
              :from="activeParameters[`${filter.id}`]['from']"
              :to="activeParameters[`${filter.id}`]['to']"
              :span="dateRangeAllowed"
              :minDate="minimumDateAllowed"
              :defaultDate="maxAllowedDate"
            >
            </RangeDatePicker>
          </div>
          <div v-else-if="filter.type == 'singleDatePicker'">
            <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
            <RangeDatePicker
              :from="activeParameters[`${filter.id}`]['from']"
              :to="activeParameters[`${filter.id}`]['to']"
              @change:date="onChangeHandler($event, filter)"
              :fullPicker="false"
              :minDate="minimumDateAllowed"
              :defaultDate="maxAllowedDate"
            >
            </RangeDatePicker>
          </div>
          <div v-else-if="filter.type == 'timePicker'">
            <p class="font-weight-thin sub-title">{{ $t(filter.title) }}</p>
            <RangeTimePicker
              @change:time="(val) => onChangeHandler(val, filter)"
              :fullPickerWidth="true"
            >
            </RangeTimePicker>
          </div>
          <div v-else-if="filter.type == 'checkbox'">
            <v-checkbox
              v-model="activeParameters[`${filter.id}`]"
              :label="$t(filter.title)"
            >
            </v-checkbox>
          </div>
          <div v-else-if="filter.type == 'switch'">
            <CustomToggle
              :labelLeftText="labelLeftText"
              :labelRightText="labelRightText"
            >
            </CustomToggle>
          </div>
        </div>
      </Card>
    </v-flex>
  </FilterComponent>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from "lodash";
const momentTz = require('moment-timezone')

export default {
  props: {
    filterProps: Array,
    active: Object,
    intervalChangeCallback: {
      type: Function
    },
    open: {
      default: false,
      type: Boolean,
    },
    maxDate: {
      type: String,
      default: ''
    }
  },
  components: {
    FilterComponent: () => import("@/components/Filter/FilterContainer"),
    RangeDatePicker: () => import("@/components/utility/RangeDatePicker.vue"),
    RangeTimePicker: () => import("@/components/utility/RangeTimePicker.vue"),
  },
  data() {
    return {
      forceReloadFilter: 0,
      currentFilter: {},
      activeParameters: {},
      dateRangeAllowed: 31,
      minimumDateAllowed: "",
    };
  },
  computed: {
    ...mapGetters(['getActivePlantTimezone']),
    maxAllowedDate() {
      let calculatedDate;
      if (this.maxDate != '') {
        calculatedDate = this.maxDate;
      } else {
        const now = momentTz();
        calculatedDate = now.tz(this.getActivePlantTimezone).format('YYYY-MM-DD');
      }
      
      return calculatedDate;
    },
    currentRouteName() {
      return this.$route.name;
    },
  },
  mounted() {
    this.initializeFilter()
  },
  methods: {
    initializeFilter() {
      
      this.activeParameters = _.cloneDeep(this.active);
      this.currentFilter = this.filterProps.filter((exp) => {
        return exp.condition
          ? exp.condition(this.activeParameters[exp.ref])
          : true
      });
      
      const {
        minimumDateAllowed,
        dateRangeAllowed
      } = this.intervalChangeCallback(this.activeParameters)
      
      this.minimumDateAllowed = minimumDateAllowed
      this.dateRangeAllowed = dateRangeAllowed
    },
    selectRange(filter) {
      if (filter.value && typeof filter.value === "function") {
        return filter["value"](this.activeParameters[filter.ref]);
      } else {
        return filter.value;
      }
    },
    applyFilter() {
      
      this.$emit("changeFilterOption", this.activeParameters);
    },
    onChangeHandler(value, filter) {
      if (filter.id instanceof Object) {
        this.$set(this.activeParameters[filter.id], "from", value.from);
        this.$set(this.activeParameters[filter.id], "to", value.to);
      } else {
        this.$set(this.activeParameters, filter.id, value);
        if (filter.dependentId) {
          this.$emit("changeFilterOption", this.activeParameters);
        }
      }

      const {
        minimumDateAllowed,
        dateRangeAllowed
      } = this.intervalChangeCallback(this.activeParameters)
      
      this.minimumDateAllowed = minimumDateAllowed
      this.dateRangeAllowed = dateRangeAllowed

      this.currentFilter = this.filterProps.filter((child) => {
        if (child.condition) {
          return child.condition(this.activeParameters[child.ref]);
        } else {
          return true;
        }
      });
      this.currentFilter.forEach((child) => {
        if (filter.id === child.ref) {
          
          if (child.type === "select") {
            let value = this.selectRange(child);
            
            if (value.includes(this.activeParameters[child.id])) {
              return
            }
            
            this.$set(this.activeParameters, child.id, value[0]);
            
            const {
              minimumDateAllowed,
              dateRangeAllowed
            } = this.intervalChangeCallback(this.activeParameters)
            
            this.minimumDateAllowed = minimumDateAllowed
            this.dateRangeAllowed = dateRangeAllowed
          } else if (
            !this.activeParameters[child.id] &&
            (child.type === "datePicker" ||
            child.type === "singleDatePicker")
          ) {
            
            let currentDateObj = {
              from: this.maxAllowedDate,
              to: this.maxAllowedDate,
            };
            
            this.$set(this.activeParameters, child.id, currentDateObj);
          } else if (!this.activeParameters[child.id] && child.type === "selectCombo") {
            let value = this.selectRange(child);
            this.$set(this.activeParameters, child.id, _.cloneDeep(value));
          }
        }
      });
    },
    toggleSelectAll(filter) {
      if (
        this.activeParameters[`${filter.id}`].length ===
        this.selectRange(filter).length
      ) {
        this.activeParameters[`${filter.id}`] = [];
      } else {
        this.activeParameters[`${filter.id}`] = _.cloneDeep(
          this.active[filter.id]
        );
      }
    },
  },
  watch: {
    active() {
      this.activeParameters = _.cloneDeep(this.active);
      this.forceReloadFilter += 1;
    },
    filterProps() {
      this.initializeFilter()
    }
  },
};
</script>